import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { ClientAuthService } from './services/auth/client-auth.service';
import { DEFAULT_INTERRUPTSOURCES, Idle } from '@ng-idle/core';
import { IdleCountdownDialogComponent } from 'reg-hub-common';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { idleTimers } from '../environments/environment';
import { NavigationHistoryService } from './services/navigation-history-service/navigation-history.service';
import { Router } from '@angular/router';
import { OrdersHubService } from './services/hubs/orders-hub/orders-hub.service';
import { MatIconRegistry } from '@angular/material/icon';
import { first } from 'rxjs';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AppComponent implements OnInit {
  title = 'reg-hub-client';
  protected isAuthenticated: boolean = false;

  idleState = 'Not started.';
  timedOut = false;
  lastPing?: Date = undefined;
  countdown: number = 0;
  dialogRef: MatDialogRef<IdleCountdownDialogComponent> | null = null;

  constructor(
    private router: Router,
    private authService: ClientAuthService,
    private idle: Idle,
    private dialog: MatDialog,
    private navigationService: NavigationHistoryService,
    private ordersHub: OrdersHubService,
    private matIconRegistry: MatIconRegistry,) {
      idle.setIdle(idleTimers.idleTimeoutInSeconds);
      idle.setTimeout(idleTimers.logoutWarningCountdownInSeconds);
      // Set default interrupts (mouse, keyboard, touch)
      idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);

      idle.onIdleEnd.subscribe(() => {
        this.idleState = 'No longer idle.';
        this.resetIdleTimer();
        this.closeDialog();
      });

      idle.onTimeout.subscribe(() => {
        this.idleState = 'Timed out!';
        this.timedOut = true;
        this.authService.logOut();
      });

      idle.onIdleStart.subscribe(() => this.idleState = "You've gone idle!");
      idle.onTimeoutWarning.subscribe((countdown) => {
        this.countdown = countdown;
        this.openOrUpdateIdleCountdownDialog(countdown);
      });
    }

  ngOnInit(): void {
    this.matIconRegistry.setDefaultFontSetClass('material-symbols-rounded');

    this.resetIdleTimer();

    this.router.events
      .pipe(first(e => e.type === 4))
      .subscribe(e => {
        this.authService.completeAuthentication().then(() => {
          // Successful exchange — clear any one-shot retry sentinel.
          sessionStorage.removeItem('authRetry');
          this.isAuthenticated = true;
          setTimeout(() => {
            this.ordersHub.connect().subscribe();
          }, 1000);
        }).catch(() => {
          if (this.authService.isLoggedIn()) {
            this.isAuthenticated = true;
            return;
          }

          // Auto-retry when a client revisits a bookmarked callback URL whose state is no longer in valid.
          // Guard with a one-shot sentinel so a genuinely-broken login can't loop.
          const alreadyRetried = sessionStorage.getItem('authRetry');
          if (!alreadyRetried) {
            sessionStorage.setItem('authRetry', '1');

            if (e.state.url.includes('sso')) {
              const splitUrl = e.state.url.split('/');
              const idp = splitUrl.at(splitUrl.length - 1);

              this.authService.logInSSO(idp);
            } else {
              this.authService.logIn();
            }
            return;
          }

          // Already retried once and still not authenticated — let the app render
          // rather than redirect-looping.
          this.isAuthenticated = true;
        });
      });
}

  resetIdleTimer(): void {
    this.idle.watch();
    this.idleState = 'Started.';
    this.timedOut = false;
  }

  openOrUpdateIdleCountdownDialog(countdown: number): void {
    if (this.dialogRef) {
      // Dialog is already open, update countdown
      this.dialogRef.componentInstance.data.countdown = countdown;
    } else {
      // Open a new dialog
      this.dialogRef = this.dialog.open(IdleCountdownDialogComponent, {
        width: '300px',
        data: { countdown }
      });

      this.dialogRef.afterClosed().subscribe(() => {
        this.dialogRef = null;
        this.resetIdleTimer();
      });
    }
  }

  closeDialog(): void {
    if (this.dialogRef) {
      this.dialogRef.close();
      this.dialogRef = null;
    }
  }
}